export type ThemeMode = 'light' | 'dark';
export type ThemeColor = 'blue' | 'purple';

export interface Theme {
    /** Stable id persisted to user settings / localStorage */
    name: string;
    /** Human label shown on the Appearance page */
    label: string;
    /** Short tagline under the label */
    description: string;
    /** Drives the light/dark app chrome (the `darkTheme` body class) */
    mode: ThemeMode;
    /** Colour family — used to find the light/dark counterpart when toggling */
    color: ThemeColor;
    /** Brand accent that recolours the whole app via --primaryColor */
    primaryColor: string;
    secondaryColor: string;
    fontColor: string;
}

// NOTE: keep the existing `lightBlue` / `lightPurple` ids — they are already
// persisted in user settings, so renaming them would reset saved preferences.
export const Themes: Theme[] = [
    {
        name: 'lightBlue',
        label: 'Light Blue',
        description: 'Professional & Clean',
        mode: 'light',
        color: 'blue',
        primaryColor: '#1557A5',
        secondaryColor: '#1557A5',
        fontColor: 'white'
    },
    {
        name: 'lightPurple',
        label: 'Light Purple',
        description: 'Bold & Vibrant',
        mode: 'light',
        color: 'purple',
        primaryColor: '#413992',
        secondaryColor: '#908FE0',
        fontColor: 'white'
    },
    {
        name: 'darkBlue',
        label: 'Dark Blue',
        description: 'Elegant & Focused',
        mode: 'dark',
        color: 'blue',
        primaryColor: '#3B82F6',
        secondaryColor: '#3B82F6',
        fontColor: 'white'
    },
    {
        name: 'darkPurple',
        label: 'Dark Purple',
        description: 'Modern & Deep',
        mode: 'dark',
        color: 'purple',
        primaryColor: '#8B7FF0',
        secondaryColor: '#908FE0',
        fontColor: 'white'
    }
]

/** Default theme used for "Reset to organization default". */
export const DEFAULT_THEME_NAME = 'lightBlue';

export const getThemeByName = (name: string): Theme =>
    Themes.find((t) => t.name === name) || Themes[0];

/** Same colour family, opposite mode — used by the header light/dark toggle. */
export const getCounterpartTheme = (name: string): Theme => {
    const current = getThemeByName(name);
    return (
        Themes.find((t) => t.color === current.color && t.mode !== current.mode) ||
        current
    );
};
